import './usuario-form-modal.js';
import type { Usuario } from '@pgcustom/shared-types';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgChart, PgKpiCard } from '@pgcustom/design-system';
import type { UsuariosApi } from './usuarios.api.js';
import type { PermissoesUsuarios } from './usuarios.permissoes.js';
import type { ErpUsuarioFormModal } from './usuario-form-modal.js';

const COLUNAS: PgTableColumn[] = [
  { key: 'nome', label: 'Nome' },
  { key: 'email', label: 'E-mail' },
  { key: 'papel', label: 'Papel' },
  { key: 'status', label: 'Status' },
];

const ROTULOS_PAPEL: Record<string, string> = {
  admin: 'Administrador',
  financeiro: 'Financeiro',
  producao: 'Produção',
  parceiros: 'Parceiros',
  marketing: 'Marketing',
  suporte: 'Suporte',
};

const CORES_PAPEL: Record<string, string> = {
  admin: 'var(--pg-color-dourado)',
  financeiro: 'var(--pg-color-sucesso)',
  producao: 'var(--pg-color-destaque)',
  parceiros: 'var(--pg-color-alerta)',
  marketing: 'var(--pg-color-erro)',
  suporte: 'var(--pg-color-texto-secundario)',
};

/**
 * `<erp-usuarios-page>` — administração de contas internas
 * (`identidade.usuarios`): listar, criar, alterar papel, ativar/
 * desativar. Recurso `auth`, só `admin` (`apps/api/src/modules/auth/
 * auth.routes.ts`). Clicar numa linha abre o modal em modo edição —
 * mesmo padrão de `<erp-estoque-page>`/insumos.
 */
export class ErpUsuariosPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _usuariosApi: UsuariosApi | null = null;
  private _permissoes: PermissoesUsuarios = { ler: false, criar: false, atualizar: false };

  private resultado: Resultado<Usuario[]> = { status: 'carregando' };
  private usuariosAtuais: Usuario[] = [];
  private modal!: ErpUsuarioFormModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set usuariosApi(valor: UsuariosApi) {
    this._usuariosApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set permissoes(valor: PermissoesUsuarios) {
    this._permissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._usuariosApi) void this.carregar();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo { display: flex; align-items: center; justify-content: space-between; gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-5, 24px); flex-wrap: wrap; }
        h1 { font-family: var(--pg-font-familia-display, system-ui); font-size: var(--pg-font-size-xl, 1.5rem); color: var(--pg-color-texto-primario, #f2f3f5); margin: 0; }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
        .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 280px); gap: var(--pg-space-4, 16px); align-items: start; }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
      </style>
      <div class="topo">
        <div>
          <h1>Usuários</h1>
          <div class="subtitulo">Contas internas do ERP — clique numa linha para editar papel/status</div>
        </div>
        ${this._permissoes.criar ? '<pg-button id="botao-novo" variant="primary">Novo usuário</pg-button>' : ''}
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-total" label="Total" icone="usuarios" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-ativos" label="Ativos" icone="usuarios" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-inativos" label="Inativos" icone="usuarios" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-papeis" label="Papéis em uso" icone="usuarios" estado="carregando"></pg-kpi-card>
      </div>

      <div class="layout">
        <pg-card padding="compacto">
          <div id="usuarios"></div>
        </pg-card>
        <pg-card padding="compacto">
          <span slot="header">Distribuição por papel</span>
          <div id="donut-papel"><pg-empty-state variant="carregando" titulo="Distribuição por papel"></pg-empty-state></div>
        </pg-card>
      </div>
      <erp-usuario-form-modal id="modal"></erp-usuario-form-modal>
    `;

    this.modal = this.root.querySelector('#modal') as ErpUsuarioFormModal;
    if (this._usuariosApi) this.modal.usuariosApi = this._usuariosApi;
    this.modal.aoSalvar = () => void this.carregar();

    this.root.querySelector('#botao-novo')?.addEventListener('pg-click', () => this.modal.abrirParaCriar());

    this.root.querySelector('#usuarios')?.addEventListener('pg-row-click', (evento) => {
      if (!this._permissoes.atualizar) return;
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const usuario = this.usuariosAtuais[index];
      if (usuario) this.modal.abrirParaEditar(usuario);
    });

    this.atualizarTabela();
  }

  private async carregar(): Promise<void> {
    const api = this._usuariosApi;
    if (!api) return;

    if (!this._permissoes.ler) {
      this.resultado = { status: 'sem-permissao' };
      this.atualizarTabela();
      return;
    }

    this.resultado = { status: 'carregando' };
    this.atualizarTabela();
    this.resultado = await api.listar();
    this.atualizarTabela();
  }

  private atualizarTabela(): void {
    const container = this.root.querySelector('#usuarios');
    if (!container) return;

    if (this.resultado.status === 'ok') {
      this.usuariosAtuais = this.resultado.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS;
      tabela.rows = this.usuariosAtuais.map((u) => ({
        nome: u.nome,
        email: u.email,
        papel: ROTULOS_PAPEL[u.papel] ?? u.papel,
        status: u.ativo ? { texto: 'Ativo', tom: 'sucesso' as const } : { texto: 'Inativo', tom: 'neutro' as const },
      }));
      this.atualizarResumo(this.usuariosAtuais);
      return;
    }

    this.usuariosAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultado, 'Usuários', 'nenhum usuário cadastrado ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-total', 'kpi-ativos', 'kpi-inativos', 'kpi-papeis']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultado.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', '0');
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
    const donut = this.root.querySelector('#donut-papel');
    if (donut) donut.innerHTML = `<pg-empty-state variant="${variant}" titulo="Distribuição por papel" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarResumo(usuarios: Usuario[]): void {
    const ativos = usuarios.filter((u) => u.ativo).length;
    const inativos = usuarios.length - ativos;
    const porPapel = new Map<string, number>();
    for (const u of usuarios) porPapel.set(u.papel, (porPapel.get(u.papel) ?? 0) + 1);

    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-total') as PgKpiCard | null)?.setAttribute('valor', String(usuarios.length));
    (this.root.querySelector('#kpi-ativos') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-ativos') as PgKpiCard | null)?.setAttribute('valor', String(ativos));
    (this.root.querySelector('#kpi-inativos') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-inativos') as PgKpiCard | null)?.setAttribute('valor', String(inativos));
    (this.root.querySelector('#kpi-papeis') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-papeis') as PgKpiCard | null)?.setAttribute('valor', String(porPapel.size));

    const donut = this.root.querySelector('#donut-papel');
    if (!donut) return;
    donut.innerHTML = '<pg-chart></pg-chart>';
    const chart = donut.querySelector('pg-chart') as PgChart;
    chart.type = 'donut';
    chart.series = [{ key: 'valor', label: '' }];
    chart.points = [...porPapel.entries()].map(([papel, quantidade]) => ({
      label: ROTULOS_PAPEL[papel] ?? papel,
      values: { valor: quantidade },
      color: CORES_PAPEL[papel] ?? 'var(--pg-color-texto-secundario)',
    }));
    chart.formatarValor = (valor) => String(valor);
  }
}

function estadoVazio<T>(resultado: Resultado<T>, titulo: string, mensagemVazio: string): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo, descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo, descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo, descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: 'Nenhum registro', descricao: mensagemVazio };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-usuarios-page', ErpUsuariosPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-usuarios-page': ErpUsuariosPage;
  }
}
